import { useState } from 'react';
import {
  ApiError,
  Avatar,
  Badge,
  Button,
  ErrorNote,
  MapLegend,
  RideMap,
  Sheet,
  Stars,
  api,
  brl,
  haptic,
  km,
  minutes,
  phone as fmtPhone,
} from '@mobi/web';
import { haversineMeters, type LatLng } from '@mobi/shared';
import type { DriverRide } from '../useDriver.js';

/** Próximo passo do motorista em cada estado, e o que o botão faz. */
const STEP: Record<string, { label: string; action: string; hint: string }> = {
  accepted: {
    label: 'Cheguei no embarque',
    action: 'arrive',
    hint: 'Vá até o passageiro. Ele está vendo você chegar no mapa.',
  },
  driver_arrived: {
    label: 'Iniciar viagem',
    action: 'start',
    hint: 'Confirme o passageiro no carro antes de iniciar.',
  },
  in_progress: {
    label: 'Finalizar corrida',
    action: 'finish',
    hint: 'Ao finalizar, aparece o valor a receber em dinheiro.',
  },
};

export interface FinishResult {
  finalFareCents: number;
  walletAppliedCents: number;
  cashDueCents: number;
}

export function DriverRideView({
  ride,
  passengerPosition,
  myPosition,
  vehicleType,
  onChanged,
  onFinished,
}: {
  ride: DriverRide;
  passengerPosition: LatLng | null;
  myPosition: LatLng | null;
  vehicleType?: 'car' | 'moto';
  onChanged: () => void;
  /** o encerramento devolve o valor a receber; a tela seguinte depende dele */
  onFinished: (result: FinishResult) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const step = STEP[ride.status];
  const goingToPickup = ride.status === 'accepted' || ride.status === 'driver_arrived';

  /** Distância até o passageiro, ao vivo — só enquanto ele ainda não embarcou. */
  const distanceToPassengerMeters =
    myPosition && passengerPosition && goingToPickup
      ? haversineMeters(myPosition, passengerPosition)
      : null;

  async function advance(action: string, confirmText?: string) {
    if (confirmText && !window.confirm(confirmText)) return;
    setBusy(true);
    setError(null);
    try {
      const result = await api<FinishResult>(`/api/rides/${ride.id}/${action}`, {
        method: 'POST',
        body: {},
      });
      haptic([10, 40, 10]);
      // Corrida encerrada sai de /rides/active, então o valor a receber só
      // existe nesta resposta. Buscar depois devolveria nada.
      if (action === 'finish') onFinished(result);
      else onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'não foi possível concluir');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex h-full flex-col">
      <div className="relative min-h-0 flex-1">
        <RideMap
          className="absolute inset-0"
          routePolyline={ride.routePolyline}
          markers={[
            { point: ride.origin, kind: 'origin' },
            { point: ride.destination, kind: 'destination' },
            ...(myPosition
              ? [{ point: myPosition, kind: 'driver' as const, vehicleType }]
              : []),
            ...(passengerPosition && goingToPickup
              ? [{ point: passengerPosition, kind: 'passenger' as const }]
              : []),
          ]}
        />
        <div className="pt-safe absolute inset-x-0 top-0 px-4">
          <div className="elevated-lg rounded-3xl border border-line/60 bg-surface/85 px-4 py-3 backdrop-blur-xl">
            {distanceToPassengerMeters !== null && (
              <div className="mb-2.5 flex items-center justify-between border-b border-line/60 pb-2.5">
                <span className="eyebrow text-faint">Até o passageiro</span>
                <span className="tabular text-sm font-bold text-brand">
                  {km(distanceToPassengerMeters)}
                </span>
              </div>
            )}
            <MapLegend
              items={[
                { kind: 'origin', label: 'Embarque' },
                { kind: 'destination', label: 'Destino' },
                { kind: 'driver', label: 'Você' },
                ...(passengerPosition && goingToPickup
                  ? [{ kind: 'passenger' as const, label: 'Passageiro' }]
                  : []),
              ]}
            />
          </div>
        </div>
      </div>

      <Sheet className="max-h-[64%] overflow-y-auto">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-xs font-medium uppercase tracking-wide text-faint">
              {goingToPickup ? 'Buscando passageiro' : 'Em viagem'}
            </p>
            <p className="truncate text-lg font-bold">
              {goingToPickup ? ride.origin.address : ride.destination.address}
            </p>
          </div>
          <p className="tabular shrink-0 text-xl font-bold">{brl(ride.estimate.fareCents)}</p>
        </div>

        <p className="tabular mt-1 text-sm text-muted">
          {km(ride.estimate.distanceMeters)} · {minutes(ride.estimate.durationSeconds)}
        </p>

        {ride.passenger && (
          <div className="mt-5 flex items-center gap-3 rounded-2xl bg-surface-2 p-4">
            <Avatar
              name={ride.passenger.name}
              src={ride.passenger.hasPhoto ? `/api/rides/${ride.id}/photo/passenger` : null}
            />
            <div className="min-w-0 flex-1">
              <p className="truncate font-semibold">{ride.passenger.name}</p>
              <Stars value={ride.passenger.rating} count={ride.passenger.ratingCount} />
            </div>
            {ride.passenger.phone && (
              <a
                href={`tel:${ride.passenger.phone}`}
                aria-label={`Ligar para ${ride.passenger.name}`}
                className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-brand"
              >
                <svg viewBox="0 0 24 24" className="h-6 w-6 fill-brand-ink" aria-hidden="true">
                  <path d="M6.6 10.8a15.1 15.1 0 0 0 6.6 6.6l2.2-2.2c.3-.3.7-.4 1-.2 1.2.4 2.4.6 3.6.6.6 0 1 .4 1 1V20c0 .6-.4 1-1 1A17 17 0 0 1 3 4c0-.6.4-1 1-1h3.5c.6 0 1 .4 1 1 0 1.3.2 2.5.6 3.6.1.4 0 .8-.2 1l-2.3 2.2Z" />
                </svg>
              </a>
            )}
          </div>
        )}

        {ride.status === 'driver_arrived' && (
          <div className="mt-4">
            <Badge tone="good">Passageiro avisado de que você chegou</Badge>
          </div>
        )}

        {step && <p className="mt-4 text-sm text-muted">{step.hint}</p>}

        {error && (
          <div className="mt-4">
            <ErrorNote>{error}</ErrorNote>
          </div>
        )}

        <div className="mt-5 space-y-3">
          {step && (
            <Button
              size="lg"
              full
              loading={busy}
              onClick={() =>
                void advance(
                  step.action,
                  step.action === 'finish' ? 'Finalizar a corrida agora?' : undefined,
                )
              }
            >
              {step.label}
            </Button>
          )}

          {/* Corrida iniciada não cancela: o servidor recusa, e o botão
              não deve prometer o que não acontece. */}
          {ride.status !== 'in_progress' && (
            <Button
              variant="ghost"
              full
              loading={busy}
              onClick={() =>
                void advance('cancel', 'Cancelar esta corrida? Isso afeta sua reputação.')
              }
            >
              Cancelar corrida
            </Button>
          )}
        </div>
      </Sheet>
    </div>
  );
}
